/**
 * Initialization Error Component
 *
 * Displays errors that occurred during the initialization phase
 */

import React from "react";

import CancelIcon from "@mui/icons-material/Cancel";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  List,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";

export interface InitializationError {
  stepId: string;
  stepName: string;
  error: string;
  details?: string;
  timestamp: string;
  severity: "error" | "warning" | "critical";
  retryable?: boolean;
}

export interface InitializationErrorProps {
  errors: InitializationError[];
  onRetry?: () => void;
  onDismiss?: () => void;
}

export const InitializationErrorComponent: React.FC<
  InitializationErrorProps
> = ({ errors, onRetry, onDismiss }) => {
  const criticalErrors = errors.filter((e) => e.severity === "critical");
  const regularErrors = errors.filter((e) => e.severity === "error");
  const warnings = errors.filter((e) => e.severity === "warning");

  const hasRetryableErrors = errors.some((e) => e.retryable);

  const _getSeverityColor = (severity: InitializationError["severity"]) => {
    switch (severity) {
      case "critical":
        return "error";
      case "error":
        return "error";
      case "warning":
        return "warning";
      default:
        return "info";
    }
  };

  const getSeverityIcon = (severity: InitializationError["severity"]) => {
    switch (severity) {
      case "critical":
      case "error":
        return <ErrorIcon />;
      case "warning":
        return <WarningIcon />;
      default:
        return <ErrorIcon />;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        p: 3,
      }}
    >
      <Card
        sx={{
          maxWidth: 800,
          width: "100%",
          boxShadow: 3,
        }}
      >
        <CardContent>
          {/* Header */}
          <Box sx={{ mb: 3, textAlign: "center" }}>
            <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom color="error">
              Initialization Failed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The application failed to initialize properly. Please review the
              errors below.
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Error Summary */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Error Summary
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              {criticalErrors.length > 0 && (
                <Chip
                  icon={<CancelIcon />}
                  label={`${criticalErrors.length} Critical Error${criticalErrors.length > 1 ? "s" : ""}`}
                  color="error"
                  variant="outlined"
                />
              )}
              {regularErrors.length > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${regularErrors.length} Error${regularErrors.length > 1 ? "s" : ""}`}
                  color="error"
                  variant="outlined"
                />
              )}
              {warnings.length > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={`${warnings.length} Warning${warnings.length > 1 ? "s" : ""}`}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {/* Critical Errors */}
          {criticalErrors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Critical Errors - Application cannot proceed
                </Typography>
              </Alert>
              <List>
                {criticalErrors.map((error, index) => (
                  <Accordion key={index} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getSeverityIcon(error.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={error.stepName}
                        secondary={error.error}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Step ID:</strong> {error.stepId}
                        </Typography>
                        {error.details && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>Details:</strong> {error.details}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          <strong>Timestamp:</strong>{" "}
                          {new Date(error.timestamp).toLocaleString()}
                        </Typography>
                        {error.retryable && (
                          <Chip
                            label="Retryable"
                            size="small"
                            color="info"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </List>
            </Box>
          )}

          {/* Regular Errors */}
          {regularErrors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Errors
                </Typography>
              </Alert>
              <List>
                {regularErrors.map((error, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getSeverityIcon(error.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={error.stepName}
                        secondary={error.error}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Step ID:</strong> {error.stepId}
                        </Typography>
                        {error.details && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>Details:</strong> {error.details}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          <strong>Timestamp:</strong>{" "}
                          {new Date(error.timestamp).toLocaleString()}
                        </Typography>
                        {error.retryable && (
                          <Chip
                            label="Retryable"
                            size="small"
                            color="info"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </List>
            </Box>
          )}

          {/* Warnings */}
          {warnings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Warnings
                </Typography>
              </Alert>
              <List>
                {warnings.map((error, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getSeverityIcon(error.severity)}
                      </ListItemIcon>
                      <ListItemText
                        primary={error.stepName}
                        secondary={error.error}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          <strong>Step ID:</strong> {error.stepId}
                        </Typography>
                        {error.details && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>Details:</strong> {error.details}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          <strong>Timestamp:</strong>{" "}
                          {new Date(error.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            {hasRetryableErrors && onRetry && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                size="large"
              >
                Retry Initialization
              </Button>
            )}
            {onDismiss && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onDismiss}
                size="large"
              >
                Dismiss
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InitializationErrorComponent;
